/**
 * Quotes Router Module
 *
 * This module provides version-aware routing for the Quotes feature.
 * It uses LaunchDarkly feature flags to determine which version (V1 or V2)
 * to render for each quote-related page.
 */

import { useFlags } from 'launchdarkly-react-client-sdk';
import { Suspense, ComponentType, LazyExoticComponent, lazy } from 'react';
import { CircularProgress, Box } from '@material-ui/core';
import { retryImport } from 'router/lazy-loading/pages';

// ============================================================================
// Loading Fallback
// ============================================================================

const LoadingFallback = () => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="center"
    height="100vh">
    <CircularProgress />
  </Box>
);

// ============================================================================
// Version HOCs
// ============================================================================

/**
 * HOC that creates a version-aware component based on the `quotesV2` feature flag.
 */
const withQuoteVersion = <P extends object>(
  V1Component: LazyExoticComponent<ComponentType<P>>,
  V2Component: LazyExoticComponent<ComponentType<P>>
) => {
  const VersionAwareComponent = (props: P) => {
    const { quotesV2 } = useFlags();

    const Component = quotesV2 ? V2Component : V1Component;

    return (
      <Suspense fallback={<LoadingFallback />}>
        <Component {...(props as any)} />
      </Suspense>
    );
  };

  return VersionAwareComponent;
};

/**
 * HOC for V2-only routes that don't have a V1 equivalent
 */
const v2Only = <P extends object>(
  V2Component: LazyExoticComponent<ComponentType<P>>
) => {
  const V2OnlyComponent = (props: P) => {
    const { quotesV2 } = useFlags();

    if (!quotesV2) {
      return null;
    }

    return (
      <Suspense fallback={<LoadingFallback />}>
        <V2Component {...(props as any)} />
      </Suspense>
    );
  };

  return V2OnlyComponent;
};

// ============================================================================
// Lazy Components
// ============================================================================

// V1 Components
const QuoteListV1 = lazy(() =>
  retryImport(() => import('pages/quotes/quote-list'))
);
const QuoteDetailsV1 = lazy(() =>
  retryImport(() => import('pages/quotes/quote-details'))
);
const QuoteReviewV1 = lazy(() =>
  retryImport(() => import('pages/quotes/quote-review'))
);

// V2 Components
const QuoteListV2 = lazy(() =>
  retryImport(() => import('pages/quotes-v2/pages/list'))
);
const QuoteDetailsV2 = lazy(() =>
  retryImport(() => import('pages/quotes-v2/pages/details'))
);
const QuoteDetailsTabV2 = lazy(() =>
  retryImport(() => import('pages/quotes-v2/pages/details/pages/quote-tab'))
);
const QuoteCatalogV2 = lazy(() =>
  retryImport(() => import('pages/quotes-v2/pages/details/pages/catalog-tab'))
);
const QuotePdfPreviewV2 = lazy(() =>
  retryImport(
    () => import('pages/quotes-v2/pages/details/pages/pdf-preview-tab')
  )
);

// ============================================================================
// Exported Routers
// ============================================================================

/** Quote List - V1/V2 based on feature flag */
export const QuoteListRouter = withQuoteVersion(QuoteListV1, QuoteListV2);

/** Quote Details - V1/V2 based on feature flag */
export const QuoteDetailsRouter = withQuoteVersion(
  QuoteDetailsV1,
  QuoteDetailsV2
);

/** Quote Details Tab - V2 only */
export const QuoteDetailsTabRouter = v2Only(QuoteDetailsTabV2);

/** Quote Catalog - V2 only */
export const QuoteCatalogRouter = v2Only(QuoteCatalogV2);

/** Quote PDF Preview - V2 only */
export const QuotePdfPreviewRouter = v2Only(QuotePdfPreviewV2);

/** Quote Review - V1/V2 (currently both use V1 review) */
export const QuoteReviewRouter = withQuoteVersion(QuoteReviewV1, QuoteReviewV1);
